import { Button, TextArea, TextInput } from '@consigli/facade';
import { useCreateActionItemMutation, useProjectId } from '@consigli/hooks';
import { type ActionItem, type ProjectParticipant } from '@consigli/types';
import { FC, useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { useMsalAuth } from '../hooks/use-msal-auth';

interface CreateActionItemFormProps {
  isOpen: boolean;
  meetingId: string;
  participants: ProjectParticipant[];
  onCreated: (item: ActionItem) => void;
  onCancel: () => void;
}

interface FormValues {
  assignee: string;
  description: string;
}

export const CreateActionItemForm: FC<CreateActionItemFormProps> = ({
  isOpen,
  meetingId,
  participants,
  onCreated,
  onCancel,
}) => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const { acquireToken, userEmail, userId } = useMsalAuth();

  const [createActionItem] = useCreateActionItemMutation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // Set when a real participant is chosen; cleared when the text is edited (custom name).
  const [selectedMeetingUserId, setSelectedMeetingUserId] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: { assignee: '', description: '' } });

  const selectedAssignee = watch('assignee');

  const filteredParticipants = participants.filter((p) => {
    if (!selectedAssignee) return true;
    const q = selectedAssignee.toLowerCase();
    return (p.name ?? '').toLowerCase().includes(q) || (p.email ?? '').toLowerCase().includes(q);
  });

  const selectParticipant = (participant: ProjectParticipant) => {
    setValue('assignee', participant.name || participant.email, { shouldValidate: true });
    setSelectedMeetingUserId(participant.id);
    setIsDropdownOpen(false);
  };

  const handleCancel = useCallback(() => {
    reset();
    setSelectedMeetingUserId(null);
    setIsDropdownOpen(false);
    onCancel();
  }, [onCancel, reset]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) handleCancel();
  };

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, handleCancel]);

  const onSubmit = async (data: FormValues) => {
    try {
      const token = await acquireToken();
      // A participant is used only when the typed text still matches the chosen
      // participant; otherwise the text is treated as a custom party name.
      const chosen = selectedMeetingUserId
        ? participants.find((p) => p.id === selectedMeetingUserId)
        : undefined;
      const matchesChosen = chosen && (chosen.name || chosen.email).trim() === data.assignee.trim();
      const assignee = data.assignee.trim();

      const createdItem = await createActionItem({
        projectId,
        meetingId,
        msAccessToken: token,
        userEmail,
        userId,
        body: {
          assignedMeetingUserId: matchesChosen ? selectedMeetingUserId : null,
          assignedCustomPartyName: !matchesChosen && assignee ? assignee : null,
          descriptionText: data.description,
          completionStatus: 'pending',
        },
      }).unwrap();

      if (createdItem) {
        onCreated(createdItem);
      }
      reset();
      onCancel();
    } catch (err) {
      console.error('Error creating action item:', err);
      toast.error(
        t('meeting-secretary.meetings.error-creating-item', 'Failed to create action item'),
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-8"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      role="presentation"
      onClick={handleOverlayClick}
      onKeyDown={undefined}
    >
      <div
        className="w-full max-w-md bg-white rounded-lg shadow-xl flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-action-item-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 id="create-action-item-title" className="text-xl font-semibold text-gray-900">
              {t('meeting-secretary.meetings.add-action-item', 'Add Action Item')}
            </h2>
          </div>
          <Button
            type="button"
            tertiary
            iconOnly
            icon={FaTimes}
            iconSize={18}
            onClick={handleCancel}
            aria-label={t('meeting-secretary.meetings.cancel', 'Cancel')}
          />
        </div>

        {/* Form body */}
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="px-6 py-5 space-y-5">
            {/* Assignee — combobox */}
            <div>
              <div className="relative">
                <Controller
                  name="assignee"
                  control={control}
                  rules={{
                    required: t(
                      'meeting-secretary.meetings.assignee-required',
                      'Assignee is required',
                    ),
                  }}
                  render={({ field }) => (
                    <TextInput
                      label={t('meeting-secretary.meetings.assigned-to', 'Assigned to')}
                      {...field}
                      autoComplete="off"
                      placeholder={t(
                        'meeting-secretary.meetings.enter-assignee',
                        'Search or type a name…',
                      )}
                      className="w-full"
                      onFocus={() => setIsDropdownOpen(true)}
                      onChange={(e) => {
                        field.onChange(e);
                        setSelectedMeetingUserId(null);
                        setIsDropdownOpen(true);
                      }}
                      onBlur={() => {
                        field.onBlur();
                        // delay so click on dropdown item fires before close
                        setTimeout(() => setIsDropdownOpen(false), 150);
                      }}
                    />
                  )}
                />

                {/* Dropdown list */}
                {isDropdownOpen && participants.length > 0 && (
                  <div className="absolute left-0 right-0 top-full mt-1 z-10 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden">
                    {filteredParticipants.length > 0 ? (
                      <ul className="max-h-44 overflow-y-auto" role="listbox">
                        {filteredParticipants.map((participant) => {
                          const label = participant.name || participant.email;
                          const isChosen = selectedMeetingUserId === participant.id;
                          return (
                            <li key={participant.id} role="option" aria-selected={isChosen}>
                              <button
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => selectParticipant(participant)}
                                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${
                                  isChosen
                                    ? 'bg-slate-50 text-[#475569] font-medium'
                                    : 'text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                <span className="w-7 h-7 rounded-full bg-slate-100 text-[#475569] flex items-center justify-center text-xs font-semibold flex-shrink-0">
                                  {label.charAt(0).toUpperCase()}
                                </span>
                                <span className="flex-1 truncate">{label}</span>
                                {isChosen && <span className="text-[#475569] text-xs">✓</span>}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p className="px-3 py-2 text-sm text-gray-400">
                        {t('meeting-secretary.meetings.no-suggestions', 'No suggestions found')}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {errors.assignee && (
                <p className="text-red-500 text-xs mt-1">{errors.assignee.message}</p>
              )}
            </div>

            {/* Action description */}
            <div>
              <TextArea
                label={t('meeting-secretary.meetings.action-description', 'Action description')}
                {...register('description', {
                  required: t(
                    'meeting-secretary.meetings.description-required',
                    'Description is required',
                  ),
                })}
                rows={4}
                placeholder={t(
                  'meeting-secretary.meetings.enter-description',
                  'What needs to be done?',
                )}
                className="w-full resize-none"
              />
              {errors.description && (
                <p className="text-red-500 text-xs mt-1">{errors.description.message}</p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex-shrink-0">
            <Button type="button" secondary rounded onClick={handleCancel}>
              {t('meeting-secretary.meetings.cancel', 'Cancel')}
            </Button>
            <Button type="submit" primary rounded>
              {t('meeting-secretary.meetings.create', 'Create')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
